import { useEffect, useLayoutEffect, useRef, useState } from 'react';

export function CollapsiblePanel({
  isOpen,
  children,
  durationMs = 300,
  maxHeightPercentage = 80,
  lockBodyScroll = false,
  className = '',
  contentKey,
}: {
  isOpen: boolean;
  children: React.ReactNode;
  durationMs?: number;
  maxHeightPercentage?: number;
  lockBodyScroll?: boolean;
  className?: string;
  contentKey?: string | number;
}) {
  const contentRef = useRef<HTMLDivElement>(null);

  const [isRendered, setIsRendered] = useState(false);
  const [heightPx, setHeightPx] = useState(0);

  const prevContentKeyRef = useRef<string | number | undefined>(contentKey);
  const openedOnceRef = useRef(false);

  useEffect(() => {
    if (!lockBodyScroll) {
      return;
    }

    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, lockBodyScroll]);

  const measure = () => {
    const el = contentRef.current;
    if (!el) {
      return 0;
    }

    const raw = el.scrollHeight;
    const max = window.innerHeight * (maxHeightPercentage / 100);
    return Math.min(raw, max);
  };

  useEffect(() => {
    if (isOpen) {
      setIsRendered(true);

      requestAnimationFrame(() => {
        setHeightPx(measure());
        openedOnceRef.current = true;
      });

      return;
    }

    if (isRendered) {
      requestAnimationFrame(() => {
        const fixed = measure();
        setHeightPx(fixed);

        requestAnimationFrame(() => {
          setHeightPx(0);
        });
      });

      const t = window.setTimeout(() => {
        setIsRendered(false);
      }, durationMs);

      return () => window.clearTimeout(t);
    }
  }, [isOpen, isRendered, durationMs, maxHeightPercentage]);

  useLayoutEffect(() => {
    if (!isOpen) {
      return;
    }

    const prevKey = prevContentKeyRef.current;
    const nextKey = contentKey;

    if (!openedOnceRef.current) {
      prevContentKeyRef.current = nextKey;
      return;
    }

    if (prevKey !== nextKey) {
      const el = contentRef.current;
      if (!el) {
        return;
      }

      const wrapper = el.parentElement as HTMLDivElement | null;
      if (!wrapper) {
        return;
      }

      const prevTransition = wrapper.style.transition;
      wrapper.style.transition = 'none';

      setHeightPx(measure());

      requestAnimationFrame(() => {
        wrapper.style.transition = prevTransition;
      });

      prevContentKeyRef.current = nextKey;
    }
  }, [contentKey, isOpen, maxHeightPercentage]);

  if (!isRendered && !isOpen) {
    return null;
  }

  return (
    <div
      className={`relative overflow-hidden transition-[max-height,opacity] ease-in-out ${className}`}
      style={{
        maxHeight: `${heightPx}px`,
        opacity: isOpen ? 1 : 0.99,
        transitionDuration: `${durationMs}ms`,
      }}
    >
      <div
        ref={contentRef}
        className="overflow-y-auto overscroll-contain"
        style={{ maxHeight: `${maxHeightPercentage}vh` }}
      >
        {children}
      </div>
    </div>
  );
}
